'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/core/presentation/components/ui/AppImage';

const steps = [
{
  number: '01',
  icon:
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>,

  title: 'Você registra o serviço realizado',
  description: 'Após instalar ou fazer uma manutenção, você cadastra o cliente no sistema. Informa o nome, o modelo do ar-condicionado (opcional), a periodicidade da próxima manutenção - 3, 6, quantos meses você quiser definir. A data do serviço é preenchida automaticamente com o dia de hoje, mas pode ser ajustada se o serviço foi feito em outra data.',
  detail: 'Leva menos de 1 minuto.',
  highlight: 'Nome do cliente + periodicidade. Só isso.',
  highlightColor: 'bg-cyan/10 border-cyan/20 text-cyan',
  image: '/assets/images/teste3_step1.png',
  imageAlt:
    'Tela Registrar serviço no RetornaAr: dados do cliente, serviço realizado, datas e próxima manutenção',
},
{
  number: '02',
  icon:
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
      </svg>,

  title: 'O sistema calcula a próxima data automaticamente',
  description:
    'Com base na data do serviço e na periodicidade que você definiu, o RetornaAr calcula quando será a próxima manutenção. Você recebe lembretes na semana em que a data se aproxima (com antecedência, para se organizar) e de novo no dia da manutenção prevista. Sem planilha, sem anotação, sem esforço da sua parte — o sistema cuida da data por você.',
  detail: 'Cálculo automático e preciso.',
  highlight: 'Zero trabalho manual. Zero esquecimento.',
  highlightColor: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
  image: '/assets/images/teste2_step2.png',
  imageAlt: 'Configuração de intervalos de manutenção com calendário visual no RetornaAr'
},
{
  number: '03',
  icon:
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
      </svg>,

  title: 'Você recebe um lembrete no WhatsApp',
  description: 'No dia certo, você recebe uma mensagem no seu próprio WhatsApp: "Hora de entrar em contato com [Nome do Cliente] — manutenção do AC vence hoje." Aí é você quem decide como e quando ligar para o cliente. O controle é todo seu.',
  detail: 'Lembrete direto no seu WhatsApp.',
  highlight: 'Você decide como contatar o cliente. Sempre.',
  highlightColor: 'bg-green-500/10 border-green-500/20 text-green-400',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_157a38d02-1772645319417.png",
  imageAlt: 'Notificação de lembrete sendo recebida no WhatsApp do dono da empresa de ar-condicionado'
}];

/** Textos do card complementar (abaixo dos 3 passos). */
const HOW_IT_WORKS_BONUS_BADGE = 'Bônus';
const HOW_IT_WORKS_BONUS_TITLE = 'Depois do lembrete, você marca o que aconteceu';
const HOW_IT_WORKS_BONUS_DESCRIPTION =
  'Indique se já entrou em contato com o cliente e, quando fechar a manutenção, registre a data agendada. O RetornaAr organiza isso numa agenda — para você enxergar a semana e as manutenções já marcadas, sem planilha à parte.';
const HOW_IT_WORKS_BONUS_HIGHLIGHT = 'Contato + manutenção agendada viram visão clara na agenda.';

const HowItWorksSection: React.FC = () => {
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            const delay = Number(el.dataset.delay || 0);
            setTimeout(() => {
              el.classList.remove('reveal-hidden');
              el.classList.add('reveal-visible');
            }, delay);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    stepsRef.current.forEach((step) => {
      if (step) observer.observe(step);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="como-funciona" className="section-padding animated-gradient-bg relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(126,207,239,0.08) 0%, transparent 70%)',
        filter: 'blur(60px)'
      }} />

      <div className="max-w-[1320px] mx-auto relative z-10">
        {/* Header */}
        <div ref={headerRef} className="reveal-hidden text-center max-w-2xl mx-auto mb-20">
          <span className="tag-badge bg-cyan/15 text-cyan border border-cyan/30 mb-5 inline-block">
            Como Funciona
          </span>
          <h2 className="font-fraunces text-white text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] mb-5">
            3 passos simples.
            <span className="italic gradient-text"> Você no controle.</span>
          </h2>
          <p className="text-white/55 text-lg font-light leading-relaxed">
            Registre o serviço uma vez. O sistema calcula tudo e te avisa na hora certa. Sem complicação.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, i) =>
          <div
            key={i}
            ref={(el) => {stepsRef.current[i] = el;}}
            data-delay={i * 180}
            className="reveal-hidden glass-panel rounded-2xl overflow-hidden">
            
              <div className={`grid md:grid-cols-2 gap-0 ${i % 2 === 1 ? 'md:[direction:rtl]' : ''}`}>
                {/* Image side */}
                <div className="relative h-64 md:h-auto overflow-hidden" style={i % 2 === 1 ? { direction: 'ltr' } : {}}>
                  <AppImage
                  src={step.image}
                  alt={step.imageAlt}
                  fill
                  className="object-cover opacity-85 transition-opacity duration-700 hover:opacity-95" />
                
                  {/* Vinheta: centro legível; bordas (e junção com o texto) um pouco mais escuras */}
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={
                      i % 2 === 1
                        ? {
                            background: [
                              'radial-gradient(ellipse 72% 78% at 50% 48%, rgba(8,15,58,0) 0%, rgba(8,15,58,0) 52%, rgba(8,15,58,0.14) 78%, rgba(8,15,58,0.42) 100%)',
                              'linear-gradient(to left, rgba(8,15,58,0.35), transparent 45%)',
                            ].join(', '),
                          }
                        : {
                            background: [
                              'radial-gradient(ellipse 72% 78% at 50% 48%, rgba(8,15,58,0) 0%, rgba(8,15,58,0) 52%, rgba(8,15,58,0.14) 78%, rgba(8,15,58,0.42) 100%)',
                              'linear-gradient(to right, rgba(8,15,58,0.35), transparent 45%)',
                            ].join(', '),
                          }
                    }
                  />
                
                  {/* Step number overlay */}
                  <div className="absolute bottom-6 left-6 font-fraunces text-7xl font-light text-white/10 leading-none select-none">
                    {step.number}
                  </div>
                </div>

                {/* Content side */}
                <div
                className="p-8 md:p-12 flex flex-col justify-center"
                style={i % 2 === 1 ? { direction: 'ltr' } : {}}>
                
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-cyan/15 border border-cyan/30 flex items-center justify-center text-cyan">
                      {step.icon}
                    </div>
                    <div>
                      <span className="text-cyan text-xs font-bold uppercase tracking-widest block">Passo {step.number}</span>
                      <span className="text-white/40 text-xs">{step.detail}</span>
                    </div>
                  </div>

                  <h3 className="font-fraunces text-white text-2xl md:text-3xl font-medium leading-snug mb-4">
                    {step.title}
                  </h3>
                  <p className="text-white/55 leading-relaxed mb-6">
                    {step.description}
                  </p>
                  <div className={`p-4 rounded-xl border ${step.highlightColor} inline-flex items-center gap-2 w-fit`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span className="text-sm font-medium">{step.highlight}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bônus: controle + agenda (secundário aos 3 passos) */}
        <div
          ref={(el) => {
            stepsRef.current[3] = el;
          }}
          data-delay={540}
          className="reveal-hidden mt-8 rounded-2xl border border-white/12 bg-white/[0.04] p-6 md:p-8 backdrop-blur-sm md:flex md:items-start md:gap-8"
        >
          <div className="mb-5 flex shrink-0 items-center justify-center md:mb-0 md:w-14 md:justify-start">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan/25 bg-cyan/10 text-cyan">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
              </svg>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <span className="mb-2 inline-block rounded-md border border-cyan/25 bg-cyan/10 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-cyan">
              {HOW_IT_WORKS_BONUS_BADGE}
            </span>
            <h3 className="font-fraunces text-xl font-medium leading-snug text-white md:text-2xl mb-3">
              {HOW_IT_WORKS_BONUS_TITLE}
            </h3>
            <p className="text-white/60 leading-relaxed mb-5 text-sm md:text-base">{HOW_IT_WORKS_BONUS_DESCRIPTION}</p>
            <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-cyan/20 bg-cyan/10 p-3 text-sm font-medium text-cyan">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M20 6L9 17l-5-5" />
              </svg>
              {HOW_IT_WORKS_BONUS_HIGHLIGHT}
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div
          className="mt-10 p-6 rounded-2xl border border-cyan/20 bg-cyan/5 text-center reveal-hidden"
          ref={(el) => {
            stepsRef.current[4] = el;
          }}
          data-delay={660}
        >
          <p className="text-white/70 text-base">
            <span className="text-cyan font-semibold">Importante:</span> O sistema não envia mensagens para os seus clientes. Ele envia o lembrete apenas para <strong className="text-white">você</strong> — o dono da empresa. Você mantém total controle sobre como e quando contatar cada cliente.
          </p>
        </div>
      </div>
    </section>);

};

export default HowItWorksSection;